// Styles
import "../css/style.css";
// Dependencies
import { gsap } from "gsap";
import CustomEase from "gsap/CustomEase";
import { ScrollTrigger } from "gsap/ScrollTrigger";
// Modules
import { initLenis, getLenis } from "./lenis";
import initPreloader, { initHeroAnimations } from "./preloader";
import initHeader from "./header";
import initTextReveals from "./textReveals";
import initImageParallax from "./imageParallax";
import initFooter from "./footer";
import initHomepage from "./homepage";
// Utils
import { cleanupAllSplits } from "./utils/splitText";

gsap.registerPlugin(CustomEase, ScrollTrigger);
CustomEase.create("smoothInOut", "0.87, 0, 0.13, 1");

// Session cookie helpers
const hasSeenIntro = () => document.cookie.split(";").some((c) => c.trim().startsWith("lr_intro_seen="));
const setIntroSeen = () => { document.cookie = "lr_intro_seen=1; path=/; samesite=lax"; };

// Initialize
const initModules = (includePreloader = false) => {
	if (includePreloader) {
		initPreloader();
	}
	initHeader();
	initHomepage();
	initTextReveals();
	initImageParallax();
	initFooter();

	ScrollTrigger.refresh();
};

const init = () => {
	resetStuff();

	requestAnimationFrame(() => {
		const lenis = getLenis();
		if (lenis) {
			lenis.scrollTo(0, { immediate: true });
		}

		const showIntro = !hasSeenIntro();
		if (showIntro) {
			setIntroSeen();
		} else {
			const loader = document.getElementById("loader");
			if (loader) loader.style.display = "none";
		}

		initModules(showIntro);

		if (!showIntro) {
			initHeroAnimations();
		}

		gsap.to(document.body, { opacity: 1, duration: 0.4, ease: "power2.out" });
	});
};

const initPageTransition = () => {
	document.addEventListener("click", (e) => {
		const link = e.target.closest("a");
		if (!link) return;
		const href = link.getAttribute("href");
		if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) return;
		if (link.target === "_blank" || link.hostname !== window.location.hostname) return;
		if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
		e.preventDefault();
		gsap.to(document.body, {
			opacity: 0,
			duration: 0.3,
			ease: "power2.in",
			onComplete: () => { window.location = href; },
		});
	});
};

//Resize Handler
let resizeTimeout;
const handleResize = () => {
	clearTimeout(resizeTimeout);
	resizeTimeout = setTimeout(() => {
		resetStuff();

		requestAnimationFrame(() => {
			initModules(false);
		});
	}, 250);
};

window.addEventListener("load", () => { init(); initPageTransition(); });
window.addEventListener("pageshow", (e) => { if (e.persisted) init(); });
window.addEventListener("resize", handleResize);

// –––––––––––––––––––––––––––––––––––––––––––––––– //
// We need to reset stuff to ensure our preloader,
// scroll triggers, etc. are ready to go
// –––––––––––––––––––––––––––––––––––––––––––––––– //
const resetStuff = () => {
	cleanupAllSplits();

	ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
	gsap.killTweensOf("*");

	if ("scrollRestoration" in window.history) {
		window.history.scrollRestoration = "manual";
	}

	initLenis();
};
